// =============================================================================
// BramkaAudio — test głośników i mikrofonu PRZED tym, jak Nexus zacznie mówić.
//
// Wymóg właściciela (13.09.2026): „na samym początku Genesis ma być
// sprawdzenie głośników i mikrofonu". Do tej pory bramka żyła wyłącznie
// w Akademii partnera (`AkademiaPage.tsx`); Kino Genesis dla Jan-Dar ruszyło
// bez niej i cała prezentacja modułów przeleciała na wyciszonym stanowisku.
//
// Tor w OBIE strony: Nexus mówi (głośniki), człowiek odpowiada (mikrofon),
// transkrypcja dowodzi, że go słychać. Wynik zapisuje się w sessionStorage
// pod JEDNYM kluczem (`KLUCZ_BRAMKI`) — Genesis i Akademia dzielą decyzję,
// więc w jednej sesji pytamy raz. „Kontynuuj bez dźwięku" zostaje jako
// uczciwe wyjście dla stanowisk bez mikrofonu: lektor startuje wyciszony.
//
// Sieć i mikrofon są wstrzykiwane (`pobierzMowe`, `transkrybuj`, `nagraj`)
// i domyślnie ładują `nexusApi` dynamicznie — komponent renderuje się
// w teście bez Supabase.
// =============================================================================

import { Check, Headphones, Loader2, Mic, Volume2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  czySlyszy,
  NAGRANIE_TESTU_MS,
  POTWIERDZENIE_TESTU,
  POWITANIE_TESTU,
  zapamietajBramke,
} from './lektorCore';
import { useLektor, type PobierzMowe } from './useLektor';

/** Fazy testu rozmowy. */
export type FazaBramki = 'wstep' | 'mowi' | 'slucha' | 'sprawdza' | 'zaliczona' | 'niezaliczona';

/** Transkrypcja nagrania (base64 + MIME) → tekst. */
export type Transkrybuj = (audioBase64: string, mimeType: string) => Promise<string>;

export interface BramkaAudioProps {
  /** Wywoływane raz, gdy bramka jest rozstrzygnięta: `lektor` = czy mówić. */
  onGotowe: (lektor: boolean) => void;
  tytul?: string;
  /** Zdanie pod tytułem — co Nexus będzie robił głosem. */
  opis?: string;
  /**
   * Organizacja, na której rachunek idą synteza i transkrypcja testu
   * (`tenant_id`, decyzja właściciela z 29.10.2026). Ta sama, co kursu/Genesis.
   */
  tenantId?: string | null;
  pobierzMowe?: PobierzMowe;
  transkrybuj?: Transkrybuj;
  /** Nagranie `ms` milisekund z mikrofonu (domyślnie MediaRecorder). */
  nagraj?: (ms: number) => Promise<Blob>;
}

/** Nagraj `ms` milisekund z mikrofonu; zwalnia urządzenie zawsze. */
export async function nagrajZMikrofonu(ms: number): Promise<Blob> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });
  try {
    const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
    const nagrywarka = new MediaRecorder(stream, { mimeType });
    const kawalki: Blob[] = [];
    nagrywarka.ondataavailable = (e) => {
      if (e.data.size > 0) kawalki.push(e.data);
    };
    const koniec = new Promise<void>((resolve) => {
      nagrywarka.onstop = () => resolve();
    });
    nagrywarka.start(250);
    await new Promise((r) => setTimeout(r, ms));
    nagrywarka.stop();
    await koniec;
    return new Blob(kawalki, { type: mimeType });
  } finally {
    stream.getTracks().forEach((t) => t.stop());
  }
}

export async function blobNaBase64(blob: Blob): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve((reader.result as string).split(',')[1] ?? '');
    reader.readAsDataURL(blob);
  });
}

/**
 * Transkrypcja przez serwer Nexusa na rachunek `tenantId` (`nexusApi` ładowany
 * dynamicznie). Bez organizacji — odmowa lokalna zamiast 400 z serwera.
 */
export function transkrybujDla(tenantId: string | null | undefined): Transkrybuj {
  return async (audioBase64, mimeType) => {
    if (!tenantId) throw new Error('Transkrypcja wymaga organizacji.');
    const { nexusPost } = await import('../../lib/nexusApi');
    const { text } = await nexusPost<{ text: string }>(
      '/api/nexus/voice/transcribe',
      { tenant_id: tenantId, audio_base64: audioBase64, language: 'pl', mime_type: mimeType },
      { timeoutMs: 45_000 }
    );
    return text ?? '';
  };
}

/** Nagranie z mikrofonu → tekst; jedna ścieżka dla bramki i pytań głosem. */
export async function nagrajITranskrybuj(
  ms: number,
  nagraj: (ms: number) => Promise<Blob>,
  transkrybuj: Transkrybuj
): Promise<string> {
  const nagranie = await nagraj(ms);
  const audioBase64 = await blobNaBase64(nagranie);
  return transkrybuj(audioBase64, nagranie.type);
}

export function BramkaAudio({
  onGotowe,
  tytul = 'Zanim zaczniemy',
  opis = 'Nexus poprowadzi Cię głosem.',
  tenantId,
  pobierzMowe,
  transkrybuj: transkrybujPodany,
  nagraj = nagrajZMikrofonu,
}: BramkaAudioProps) {
  const [faza, setFaza] = useState<FazaBramki>('wstep');
  const [blad, setBlad] = useState<string | null>(null);
  const { mow, zatrzymaj } = useLektor({ pobierzMowe, tenantId });
  const transkrybuj = transkrybujPodany ?? transkrybujDla(tenantId);
  const zywyRef = useRef(true);
  useEffect(
    () => () => {
      zywyRef.current = false;
    },
    []
  );

  const testujRozmowe = useCallback(async () => {
    setBlad(null);
    setFaza('mowi');
    const wybrzmialo = await mow('bramka:powitanie', POWITANIE_TESTU);
    if (!zywyRef.current) return;
    if (!wybrzmialo) {
      setBlad('Nie udało się odezwać głosem Nexusa — spróbuj jeszcze raz za chwilę.');
      setFaza('niezaliczona');
      return;
    }

    setFaza('slucha');
    let nagranie: Blob;
    try {
      nagranie = await nagraj(NAGRANIE_TESTU_MS);
    } catch {
      if (!zywyRef.current) return;
      setBlad('Brak dostępu do mikrofonu. Zezwól przeglądarce na mikrofon i spróbuj ponownie.');
      setFaza('niezaliczona');
      return;
    }

    setFaza('sprawdza');
    try {
      const tekst = await transkrybuj(await blobNaBase64(nagranie), nagranie.type);
      if (!zywyRef.current) return;
      if (!czySlyszy(tekst)) {
        setBlad('Nie usłyszałem Cię. Sprawdź mikrofon i powiedz wyraźnie: „zaczynamy".');
        setFaza('niezaliczona');
        return;
      }
    } catch {
      if (!zywyRef.current) return;
      setBlad('Nie udało się sprawdzić nagrania — spróbuj jeszcze raz.');
      setFaza('niezaliczona');
      return;
    }

    zapamietajBramke('glos');
    // Potwierdzenie wybrzmiewa PRZED oddaniem sterów — puszczone w tle
    // ścigałoby się z pierwszą kwestią lektora i ucinało jedno z dwojga.
    setFaza('zaliczona');
    await mow('bramka:potwierdzenie', POTWIERDZENIE_TESTU);
    if (!zywyRef.current) return;
    onGotowe(true);
  }, [mow, nagraj, transkrybuj, onGotowe]);

  const kontynuujBezDzwieku = useCallback(() => {
    zapamietajBramke('cisza');
    zatrzymaj();
    onGotowe(false);
  }, [zatrzymaj, onGotowe]);

  return (
    <div className="card w-full max-w-xl p-8 md:p-10 text-center" aria-label="Sprawdzenie dźwięku">
      <Headphones className="w-10 h-10 mx-auto mb-4 text-aurora-brand" aria-hidden="true" />
      <h2 className="text-2xl font-semibold mb-3">{tytul}</h2>
      <p className="text-aurora-muted mb-6">
        {opis} <strong>Włącz głośniki</strong> lub podłącz słuchawki i{' '}
        <strong>włącz mikrofon</strong> — zaczniemy po krótkim teście rozmowy, gdy Nexus Cię
        usłyszy.
      </p>

      {faza === 'wstep' && (
        <button onClick={() => void testujRozmowe()} className="btn btn--primary">
          <Mic className="w-4 h-4" aria-hidden="true" /> Przetestuj rozmowę z Nexusem
        </button>
      )}

      {faza === 'mowi' && (
        <p className="flex items-center justify-center gap-2 text-aurora-text">
          <Volume2 className="w-5 h-5 text-aurora-brand animate-pulse" aria-hidden="true" />
          Nexus mówi — sprawdź, czy go słyszysz…
        </p>
      )}

      {faza === 'slucha' && (
        <p className="flex items-center justify-center gap-2 text-aurora-text">
          <Mic className="w-5 h-5 text-aurora-brand animate-pulse" aria-hidden="true" />
          Teraz powiedz: <strong>„zaczynamy"</strong>
        </p>
      )}

      {faza === 'sprawdza' && (
        <p className="flex items-center justify-center gap-2 text-aurora-muted">
          <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
          Sprawdzam, czy Cię słyszę…
        </p>
      )}

      {faza === 'zaliczona' && (
        <p className="flex items-center justify-center gap-2 text-aurora-success">
          <Check className="w-5 h-5" aria-hidden="true" />
          Słyszę Cię! Zaczynamy…
        </p>
      )}

      {faza === 'niezaliczona' && (
        <div className="space-y-4">
          {blad && <p className="text-sm text-aurora-danger">{blad}</p>}
          <button onClick={() => void testujRozmowe()} className="btn btn--primary">
            <Mic className="w-4 h-4" aria-hidden="true" /> Spróbuj jeszcze raz
          </button>
        </div>
      )}

      {(faza === 'wstep' || faza === 'niezaliczona') && (
        <p className="mt-5">
          <button
            onClick={kontynuujBezDzwieku}
            className="text-xs text-aurora-muted underline hover:text-aurora-text"
          >
            Kontynuuj bez dźwięku (lektor wyłączony)
          </button>
        </p>
      )}
    </div>
  );
}

export default BramkaAudio;
